import React, { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { FileText, Plus, RefreshCw, Download, AlertTriangle, CheckCircle, Clock, Edit3, MessageSquare, CalendarDays, X } from 'lucide-react';
import PageHeader from './PageHeader';
import { API, authHeaders } from '../apiClient';

function api(path, options = {}) {
  return fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(options.headers || {})
    }
  }).then(async r => {
    if (!r.ok) throw new Error((await r.json()).detail || 'API error');
    return r.json();
  });
}

function todayIso() { return new Date().toISOString().slice(0, 10); }
function money(v) { return `${Number(v || 0).toFixed(2).replace('.', ',')} PLN`; }
function nights(a, b) {
  const start = new Date(a); const end = new Date(b);
  const n = Math.round((end - start) / 86400000);
  return Number.isFinite(n) && n > 0 ? n : 1;
}
function reservationNumber(b) {
  const v = b?.external_id || b?.external_booking_id || b?.booking_external_id || '';
  return String(v).startsWith('booking:') ? String(v).slice(8) : (v || b?.id || b?.booking_id || '');
}
function bookingLink(item) {
  const ref = item.booking_external_id || item.external_booking_id || item.booking_id;
  return ref ? `/rezerwacje/${encodeURIComponent(ref)}` : '';
}
function messageLink(item) {
  return item.guest_message_id ? `/wiadomosci#message-${encodeURIComponent(item.guest_message_id)}` : '';
}
function statusLabel(status) {
  return {
    draft: ['Szkic', 'neutral'],
    queued: ['W kolejce', 'warning'],
    issued: ['Wystawiony', 'success'],
    voided: ['Unieważniony', 'danger'],
    error: ['Błąd', 'danger'],
    cancelled: ['Anulowany', 'neutral'],
  }[status] || [status || '—', 'neutral'];
}

const emptyForm = () => ({
  buyer_name: '', buyer_address: '', buyer_tax_id: '', buyer_email: '',
  guest_name: '', stay_from: '', stay_to: '', sale_date: todayIso(),
  quantity: 1, unit_price: '', total_amount: '', payment_method: 'Online Booking.com',
  payment_reference: '', notes: '', status: 'queued'
});

export default function Billing() {
  const ctx = useOutletContext() || {};
  const bookings = ctx.bookings || [];
  const [items, setItems] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm());

  async function load() {
    setError('');
    try { setItems(await api('/billing/requests')); }
    catch (e) { setError(e.message); }
  }
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const queued = items.filter(i => i.status === 'queued').length;
    const issued = items.filter(i => i.status === 'issued').length;
    const drafts = items.filter(i => i.status === 'draft').length;
    const errors = items.filter(i => i.status === 'error').length;
    return [
      { label: 'Szkice', value: drafts, tone: drafts ? 'warning' : 'info' },
      { label: 'W kolejce', value: queued, tone: queued ? 'warning' : 'info' },
      { label: 'Wystawione', value: issued, tone: 'success' },
      { label: 'Błędy', value: errors, tone: errors ? 'danger' : 'info' },
    ];
  }, [items]);

  function resetForm() {
    setEditingId(null);
    setSelectedBookingId('');
    setForm(emptyForm());
    setFormOpen(false);
  }

  function newRequest() {
    setEditingId(null);
    setSelectedBookingId('');
    setForm(emptyForm());
    setFormOpen(true);
  }

  function editRequest(item) {
    setEditingId(item.id);
    setSelectedBookingId(item.booking_id ? String(item.booking_id) : '');
    setForm({
      buyer_name: item.buyer_name || '',
      buyer_address: item.buyer_address || '',
      buyer_tax_id: item.buyer_tax_id || '',
      buyer_email: item.buyer_email || '',
      guest_name: item.guest_name || item.booking_guest_name || '',
      stay_from: item.stay_from || item.booking_checkin || '',
      stay_to: item.stay_to || item.booking_checkout || '',
      sale_date: item.sale_date || item.stay_to || item.stay_from || todayIso(),
      quantity: item.quantity || nights(item.stay_from, item.stay_to),
      unit_price: item.unit_price || '',
      total_amount: item.total_amount || '',
      payment_method: item.payment_method || 'Online Booking.com',
      payment_reference: item.payment_reference || reservationNumber(item),
      notes: item.notes || '',
      status: item.status === 'issued' ? 'issued' : (item.status || 'draft')
    });
    setFormOpen(true);
    setTimeout(() => document.getElementById('billing-edit-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }

  function pickBooking(id) {
    setSelectedBookingId(id);
    const b = bookings.find(x => String(x.id) === String(id));
    if (!b) return;
    const q = nights(b.checkin, b.checkout);
    setForm(f => ({
      ...f,
      buyer_name: f.buyer_name || b.guest_name || '',
      guest_name: b.guest_name || '',
      stay_from: b.checkin || '',
      stay_to: b.checkout || '',
      sale_date: b.checkout || b.checkin || todayIso(),
      quantity: q,
      payment_method: f.payment_method || 'Online Booking.com',
      payment_reference: reservationNumber(b),
    }));
  }

  function setAmount(value) {
    const q = Number(form.quantity || 1) || 1;
    setForm(f => ({...f, total_amount: value, unit_price: value ? (Number(value) / q).toFixed(2) : ''}));
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const payload = {...form, booking_id: selectedBookingId ? Number(selectedBookingId) : null};
      if (editingId) {
        await api(`/billing/requests/${editingId}`, {method:'PATCH', body: JSON.stringify(payload)});
      } else {
        await api('/billing/requests', {method:'POST', body: JSON.stringify(payload)});
      }
      resetForm();
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function issue(id) {
    setBusy(true); setError('');
    try { await api(`/billing/requests/${id}/issue`, {method:'POST'}); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function downloadDoc(item) {
    if (!item.document_path) return;
    const parts = item.document_path.split('/');
    const filename = parts.pop();
    const year = parts.pop();
    setError('');
    try {
      const r = await fetch(`${API}/billing/documents/${year}/${filename}`, {headers: authHeaders()});
      if (!r.ok) throw new Error((await r.json()).detail || 'Nie udało się pobrać PDF');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
  }

  return (
    <div className="page-stack">
      <PageHeader
        icon={<FileText />}
        title="Rachunki"
        description="Baza próśb gości, numeracja rachunków i repozytorium wygenerowanych PDF. Mateusz uzupełnia szkice, a pozycje w kolejce może wystawić do PDF."
        helpKey="billing"
        stats={stats}
        actions={<><button onClick={load}><RefreshCw size={16}/> Odśwież</button><button className="primary" onClick={newRequest}><Plus size={16}/> Nowy rachunek</button></>}
      />
      {error && <div className="alert error"><AlertTriangle size={16}/> {error}</div>}
      {formOpen && (
        <form id="billing-edit-form" className="card billing-form" onSubmit={submit}>
          <div className="section-head">
            <div>
              <h2>{editingId ? `Edycja prośby #${editingId}` : 'Nowa prośba o rachunek'}</h2>
              <p className="muted">Status „Szkic” pozwala zapisać niepełne dane; do kolejki Mateusza potrzebna jest kwota i daty pobytu.</p>
            </div>
            <button type="button" className="secondary" onClick={resetForm}><X size={16}/> Zamknij</button>
          </div>
          <label>Rezerwacja
            <select value={selectedBookingId} onChange={e => pickBooking(e.target.value)} disabled={form.status === 'issued'}>
              <option value="">Bez rezerwacji / ręcznie</option>
              {bookings.map(b => <option key={b.id} value={b.id}>{reservationNumber(b)} · {b.checkin}–{b.checkout} · {b.guest_name}</option>)}
            </select>
          </label>
          <div className="form-grid three">
            <label>Status
              <select value={form.status} onChange={e=>setForm({...form,status:e.target.value})} disabled={form.status === 'issued'}>
                <option value="draft">Szkic</option>
                <option value="queued">W kolejce Mateusza</option>
                <option value="cancelled">Anulowany</option>
                {form.status === 'issued' && <option value="issued">Wystawiony</option>}
              </select>
            </label>
            <label>Nabywca / firma / osoba<input required value={form.buyer_name} onChange={e=>setForm({...form,buyer_name:e.target.value})}/></label>
            <label>NIP (opcjonalnie)<input value={form.buyer_tax_id} onChange={e=>setForm({...form,buyer_tax_id:e.target.value})}/></label>
          </div>
          <div className="form-grid two">
            <label>Email nabywcy<input value={form.buyer_email} onChange={e=>setForm({...form,buyer_email:e.target.value})}/></label>
            <label>Gość<input value={form.guest_name} onChange={e=>setForm({...form,guest_name:e.target.value})}/></label>
          </div>
          <label>Adres nabywcy<textarea rows="3" value={form.buyer_address} onChange={e=>setForm({...form,buyer_address:e.target.value})}/></label>
          <div className="form-grid three">
            <label>Pobyt od<input type="date" required={form.status === 'queued'} value={form.stay_from} onChange={e=>setForm({...form,stay_from:e.target.value, quantity:nights(e.target.value, form.stay_to)})}/></label>
            <label>Pobyt do<input type="date" required={form.status === 'queued'} value={form.stay_to} onChange={e=>setForm({...form,stay_to:e.target.value, quantity:nights(form.stay_from, e.target.value)})}/></label>
            <label>Data sprzedaży<input type="date" required={form.status === 'queued'} value={form.sale_date} onChange={e=>setForm({...form,sale_date:e.target.value})}/></label>
          </div>
          <div className="form-grid three">
            <label>Ilość dób<input type="number" step="1" min="1" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})}/></label>
            <label>Kwota łączna<input type="number" step="0.01" min="0" required={form.status === 'queued'} value={form.total_amount} onChange={e=>setAmount(e.target.value)}/></label>
            <label>Metoda płatności<input value={form.payment_method} onChange={e=>setForm({...form,payment_method:e.target.value})}/></label>
          </div>
          <label>Numer płatności / rezerwacji<input value={form.payment_reference} onChange={e=>setForm({...form,payment_reference:e.target.value})}/></label>
          <label>Uwagi<input value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/></label>
          <div className="button-row"><button disabled={busy || form.status === 'issued'} className="primary">{editingId ? 'Zapisz zmiany' : 'Dodaj prośbę'}</button></div>
        </form>
      )}
      <div className="card table-card">
        <div className="table-scroll">
          <table className="data-table">
            <thead><tr><th>Status</th><th>Nr</th><th>Nabywca</th><th>Pobyt / źródła</th><th>Kwota</th><th>Płatność</th><th>Dokument</th><th>Akcje</th></tr></thead>
            <tbody>
              {items.map(item => {
                const [label, tone] = statusLabel(item.status);
                const bLink = bookingLink(item);
                const mLink = messageLink(item);
                return <tr key={item.id}>
                  <td><span className={`pill ${tone}`}>{item.status === 'issued' ? <CheckCircle size={13}/> : <Clock size={13}/>} {label}</span>{item.error && <div className="small danger-text">{item.error}</div>}</td>
                  <td>{item.number || `#${item.id}`}</td>
                  <td><strong>{item.buyer_name}</strong><div className="small">{item.buyer_tax_id ? `NIP ${item.buyer_tax_id}` : item.buyer_email}</div><div className="small muted">Gość: {item.guest_name || item.booking_guest_name || '—'}</div></td>
                  <td>
                    {item.stay_from || item.booking_checkin || '—'}–{item.stay_to || item.booking_checkout || '—'}
                    <div className="billing-links-row">
                      {bLink ? <Link className="text-link" to={bLink}><CalendarDays size={13}/> Rezerwacja {reservationNumber(item)}</Link> : <span className="small muted">Brak linku do rezerwacji</span>}
                    </div>
                    <div className="billing-links-row">
                      {mLink ? <Link className="text-link" to={mLink}><MessageSquare size={13}/> Wiadomość #{item.guest_message_id}</Link> : <span className="small muted">Brak wiadomości źródłowej</span>}
                    </div>
                  </td>
                  <td>{money(item.total_amount)}</td>
                  <td>{item.payment_method}<div className="small">{item.payment_reference}</div></td>
                  <td>{item.document_filename || '—'}</td>
                  <td className="actions-cell">
                    <button type="button" disabled={busy || item.status === 'issued'} onClick={() => editRequest(item)}><Edit3 size={14}/> Edytuj</button>
                    {item.status !== 'issued' && item.status !== 'cancelled' && item.status !== 'voided' && (item.total_amount > 0 ? <button disabled={busy} onClick={() => issue(item.id)}>Wystaw teraz</button> : <span className="muted small">Uzupełnij kwotę z rezerwacji, by wystawić</span>)}
                    {item.document_path && <button onClick={() => downloadDoc(item)}><Download size={14}/> PDF</button>}
                  </td>
                </tr>;
              })}
              {!items.length && <tr><td colSpan="8" className="empty-cell">Brak rachunków.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
